import React, { useEffect } from 'react';
import setSeo from '../../utils/seo.js';
import BackToTop from '../../components/BackToTop.jsx';
import case4 from '../../assets/case4.webp';

/**
 * Individual case study: Media & Photography Industry – AI Orchestrator for Finance Workflows
 * Full case study page with detailed implementation and results
 */
const FinanceAutomation = () => {
  useEffect(() => {
    setSeo({
      title: 'Finance Workflow Automation Case Study - 80% Less Manual Work | Aakeeo',
      description:
        'Learn how our AI-powered finance automation system reduced manual invoice work by 80% while maintaining 100% data privacy through offline processing for a media company.',
      keywords: ['finance automation', 'invoice automation', 'AI finance', 'workflow automation', 'accounting automation', 'finance case study', 'privacy-first automation'],
      canonical: window.location.href,
      og: {
        type: 'article',
        image: 'https://aakeeo.com' + case4
      },
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'Article',
        'headline': 'Media & Photography Industry – AI Orchestrator for Finance Workflows',
        'description': 'An AI-powered finance automation system that reduced manual invoice work by 80% with 100% data privacy through offline processing.',
        'image': 'https://aakeeo.com' + case4,
        'author': {
          '@type': 'Organization',
          'name': 'Aakeeo Technologies'
        },
        'publisher': {
          '@type': 'Organization',
          'name': 'Aakeeo Technologies',
          'logo': {
            '@type': 'ImageObject',
            'url': 'https://aakeeo.com/Aakeeo_favicon.png'
          }
        }
      }
    });
  }, []);
  return (
    <div className="case-study-page">
      <section className="page-hero case-studies-hero">
        <video className="page-hero-video" autoPlay muted loop>
          <source src="/background.mp4" type="video/mp4" />
        </video>
        <div className="page-hero-content">
          <h1 className="page-hero-title">Media & Photography Industry – AI Orchestrator for Finance Workflows</h1>
          <p className="page-hero-subtitle">An AI-powered finance automation system that reduced manual invoice work by 80% with 100% data privacy through offline processing.</p>
        </div>
      </section>

      <article className="section reveal">
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 2rem' }}>
          <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
            <img
              src={case4}
              alt="Finance Automation Success Story"
              style={{
                width: '100%',
                maxWidth: '600px',
                height: '300px',
                objectFit: 'cover',
                borderRadius: '15px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
              }}
            />
          </div>

          <div style={{ fontSize: '1.1rem', lineHeight: '1.8', color: 'var(--text-light)' }}>
            <h2 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.8rem' }}>
              Challenge
            </h2>

            <p style={{ marginBottom: '1.5rem' }}>
              A photo studio struggled with finance operations due to high volumes of invoices and reimbursements:
            </p>

            <ul style={{ marginBottom: '1.5rem', paddingLeft: '2rem', listStyle: 'disc' }}>
              <li style={{ marginBottom: '0.5rem' }}><strong>Invoice Dispatch:</strong> Finance teams had to manually verify invoices, cross-check client data, and send mails.</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>Reimbursements:</strong> Large volumes of hotel/travel/model bills came via email and had to be manually consolidated.</li>
            </ul>

            <p style={{ marginBottom: '1.5rem' }}>
              The client demanded a privacy-first solution where all processing stayed offline.
            </p>

            <h2 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.8rem' }}>
              Requirements
            </h2>

            <ul style={{ marginBottom: '1.5rem', paddingLeft: '2rem', listStyle: 'disc' }}>
              <li style={{ marginBottom: '0.5rem' }}>Automate invoice processing with OCR + database matching</li>
              <li style={{ marginBottom: '0.5rem' }}>Handle reimbursement workflows with monthly consolidation</li>
              <li style={{ marginBottom: '0.5rem' }}>Ensure complete offline data processing with local LLMs</li>
              <li style={{ marginBottom: '0.5rem' }}>Provide an orchestrator agent to manage/monitor sub-workflows</li>
            </ul>

            <h2 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.8rem' }}>
              Solution Architecture
            </h2>

            <p style={{ marginBottom: '1.5rem' }}>
              We built an AI Orchestrator with n8n controlling multiple sub-workflows:
            </p>

            <h3 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.4rem' }}>
              1. Invoice Processing Sub-Workflow
            </h3>
            <ul style={{ marginBottom: '1.5rem', paddingLeft: '2rem', listStyle: 'disc' }}>
              <li style={{ marginBottom: '0.5rem' }}>Invoices dumped into system</li>
              <li style={{ marginBottom: '0.5rem' }}>OCR performed with Donut (fine-tuned model) and Tesseract for fallbacks</li>
              <li style={{ marginBottom: '0.5rem' }}>Extracted details matched with MySQL client database</li>
              <li style={{ marginBottom: '0.5rem' }}>Validated invoices sent automatically via SMTP mail</li>
              <li style={{ marginBottom: '0.5rem' }}>Status logs emailed to finance team</li>
            </ul>

            <h3 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.4rem' }}>
              2. Reimbursement Sub-Workflow
            </h3>
            <ul style={{ marginBottom: '1.5rem', paddingLeft: '2rem', listStyle: 'disc' }}>
              <li style={{ marginBottom: '0.5rem' }}>Custom n8n node monitors Gmail inbox for invoices/bills</li>
              <li style={{ marginBottom: '0.5rem' }}>Downloads and categorizes them</li>
              <li style={{ marginBottom: '0.5rem' }}>Monthly batch process compiles all invoices → Sends to finance team</li>
            </ul>

            <h3 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.4rem' }}>
              3. Orchestrator Agent
            </h3>
            <ul style={{ marginBottom: '1.5rem', paddingLeft: '2rem', listStyle: 'disc' }}>
              <li style={{ marginBottom: '0.5rem' }}>Local LLM orchestrator (running on GPU server)</li>
              <li style={{ marginBottom: '0.5rem' }}>Accepts admin queries (e.g., "Run reimbursement workflow", "Check invoice batch status")</li>
              <li style={{ marginBottom: '0.5rem' }}>Activates or monitors workflows as required</li>
            </ul>

            <h2 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.8rem' }}>
              Technologies Used
            </h2>

            <ul style={{ marginBottom: '1.5rem', paddingLeft: '2rem', listStyle: 'disc' }}>
              <li style={{ marginBottom: '0.5rem' }}>n8n for orchestration and custom node development</li>
              <li style={{ marginBottom: '0.5rem' }}>Donut OCR (PyTorch model) fine-tuned on invoice dataset</li>
              <li style={{ marginBottom: '0.5rem' }}>Tesseract OCR as backup</li>
              <li style={{ marginBottom: '0.5rem' }}>Local LLMs (LLaMA / Mistral) for offline orchestration and NLP tasks</li>
              <li style={{ marginBottom: '0.5rem' }}>PostgreSQL / MySQL for structured invoice data storage</li>
              <li style={{ marginBottom: '0.5rem' }}>Dockerized environment for local deployment</li>
            </ul>

            <h2 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.8rem' }}>
              Impact
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              <div style={{
                background: 'rgba(230, 57, 70, 0.1)',
                padding: '1.5rem',
                borderRadius: '10px',
                textAlign: 'center'
              }}>
                <h3 style={{ color: 'var(--primary-color)', fontSize: '2rem', marginBottom: '0.5rem' }}>80%</h3>
                <p style={{ margin: 0 }}>Reduction in Manual Invoice Work</p>
              </div>
              <div style={{
                background: 'rgba(0, 119, 182, 0.1)',
                padding: '1.5rem',
                borderRadius: '10px',
                textAlign: 'center'
              }}>
                <h3 style={{ color: 'var(--accent-color)', fontSize: '2rem', marginBottom: '0.5rem' }}>{'95%+'}</h3>
                <p style={{ margin: 0 }}>OCR Accuracy</p>
              </div>
              <div style={{
                background: 'rgba(72, 202, 228, 0.1)',
                padding: '1.5rem',
                borderRadius: '10px',
                textAlign: 'center'
              }}>
                <h3 style={{ color: 'var(--tertiary-color)', fontSize: '2rem', marginBottom: '0.5rem' }}>3x</h3>
                <p style={{ margin: 0 }}>Finance Team Productivity</p>
              </div>
            </div>

            <ul style={{ marginBottom: '1.5rem', paddingLeft: '2rem', listStyle: 'disc' }}>
              <li style={{ marginBottom: '0.5rem' }}>Monthly reimbursements consolidated with zero manual intervention</li>
              <li style={{ marginBottom: '0.5rem' }}>Ensured 100% data privacy with offline-only processing</li>
            </ul>

            <div style={{
              background: 'rgba(230, 57, 70, 0.1)',
              padding: '1.5rem',
              borderRadius: '10px',
              borderLeft: '4px solid var(--primary-color)',
              marginTop: '2rem'
            }}>
              <p style={{ margin: 0, fontStyle: 'italic' }}>
                Ready to automate your finance workflows while maintaining complete data privacy? Contact Aakeeo today to discover how our AI orchestration solutions can transform your invoice processing and operational efficiency.
              </p>
            </div>
          </div>
        </div>
      </article>
        <BackToTop />
    </div>
  );
};

export default FinanceAutomation;